// screens/business/BusinessSubscriptionSystem.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, ScrollView, SafeAreaView,
  Modal, ActivityIndicator, Animated, Dimensions, StatusBar, Alert, FlatList,
  Switch, Linking,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import StripePaymentIntegration from './BusinessStripePayment';
import { COMMERCE_DISABLED, CommerceUnavailable } from '../../../utils/iosCommerce';

const { width, height } = Dimensions.get('window');
const API_BASE = 'https://api.jobeng.ai/api';
const API_KEY  = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const C = {
  primary:  '#0e4e96',
  p2:       '#1e7be2',
  gold:     '#fdb913',
  success:  '#10B981',
  warning:  '#F59E0B',
  danger:   '#EF4444',
  purple:   '#7C3AED',
  teal:     '#19abb5',
  bg:       '#F0F4FA',
  card:     '#FFFFFF',
  border:   '#E2E8F0',
  text:     '#1E293B',
  sub:      '#64748B',
  light:    '#94A3B8',
};

const PLAN_COLORS = {
  single_starter:      ['#0e4e96','#1e7be2'],
  single_professional: ['#7C3AED','#9F67FF'],
  single_enterprise:   ['#0F172A','#1e4080'],
  team_starter:        ['#0e9aa7','#19abb5'],
  team_professional:   ['#059669','#10B981'],
  team_enterprise:     ['#B45309','#F59E0B'],
  multi_team:          ['#0e9aa7','#19abb5'],
  multi_business:      ['#059669','#10B981'],
  multi_enterprise:    ['#B45309','#F59E0B'],
};

const PLAN_ICONS = {
  single_starter: 'rocket-launch-outline',
  single_professional: 'briefcase-outline',
  single_enterprise: 'office-building-outline',
  team_starter: 'account-group-outline',
  team_professional: 'crown-outline',
  team_enterprise: 'shield-crown-outline',
};

const getHeaders = async () => {
  const tok = await AsyncStorage.getItem('auth_token') || await AsyncStorage.getItem('token') || '';
  return { 'X-API-Key': API_KEY, 'Authorization': `Bearer ${tok}`, 'Content-Type': 'application/json' };
};

const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const diff = new Date(dateStr) - new Date();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

export default function BusinessSubscriptionSystem({ navigation, route }) {
  if (COMMERCE_DISABLED) return <CommerceUnavailable navigation={navigation} />;
  const { currentPlan, companyProfile, onPlanSelected } = route?.params || {};

  const [tab, setTab]               = useState('plans');   // plans | current | history
  const [planType, setPlanType]     = useState('single');
  const [billing, setBilling]       = useState('monthly');
  const [plans, setPlans]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [upgrading, setUpgrading]   = useState(null);
  const [subscription, setSub]      = useState(currentPlan || null);
  const [history, setHistory]       = useState([]);
  const [showModal, setShowModal]       = useState(false);
  const [modalPlan, setModalPlan]       = useState(null);
  const [bizId, setBizId]               = useState(companyProfile?.id || null);
  const [showStripe, setShowStripe]     = useState(false);
  const [stripeScenario, setScenario]   = useState(null);
  const [pendingSubData, setPendingSub] = useState(null); // subscription saved but not activated
  const [pendingPay, setPendingPay]     = useState(null);  // { sessionId, planName } awaiting external Stripe checkout
  const [checkingPay, setCheckingPay]   = useState(false);
  const fadeAnim   = useRef(new Animated.Value(0)).current;
  const submitting = useRef(false); // guard against double-tap

  useEffect(() => {
    boot();
    Animated.timing(fadeAnim, { toValue: 1, duration: 500, useNativeDriver: true }).start();
  }, []);

  const boot = async () => {
    setLoading(true);
    try {
      let cid = bizId;
      if (!cid) {
        const cp = await AsyncStorage.getItem('company_profile');
        if (cp) { const p = JSON.parse(cp); cid = p.id; setBizId(p.id); }
        else { cid = await AsyncStorage.getItem('company_id'); setBizId(cid); }
      }
      if (!subscription) {
        const cached = await AsyncStorage.getItem('business_subscription');
        if (cached) setSub(JSON.parse(cached));
      }
      // Always fetch real subscription from server to get actual price_paid
      // NEW ENDPOINT: /api/business/subscription returns single object with derived flags
      // (is_active, is_in_grace_period, days_until_expiry, days_until_renewal, auto_renew, etc.)
      if (cid) {
        try {
          const h = await getHeaders();
          const sr = await fetch(`${API_BASE}/business/subscription?company_id=${cid}`, { headers: h });
          const sd = await sr.json();
          if (sd.success && sd.subscription) {
            const serverSub = sd.subscription;
            serverSub.price = parseFloat(serverSub.price_paid || serverSub.plan_price || serverSub.price || 0);
            setSub(serverSub);
            await AsyncStorage.setItem('business_subscription', JSON.stringify(serverSub));
            console.log('[Sub] Loaded: £' + (serverSub.price_paid || serverSub.plan_price) + '/' + (serverSub.billing_cycle || 'mo') + ' • auto_renew=' + serverSub.auto_renew);
          }
        } catch (e) { console.warn('[Sub] Server fetch:', e.message); }
      }
      await Promise.all([fetchPlans(), cid && fetchHistory(cid)]);
    } finally { setLoading(false); }
  };

  const fetchPlans = async () => {
    try {
      const h = await getHeaders();
      const r = await fetch(`${API_BASE}/business/subscription/plans`, { headers: h });
      const d = await r.json();
      // New endpoint returns { success, plans: [...] }; old returned { success, data: [...] }
      if (d.success) setPlans(d.plans || d.data || []);
    } catch (e) { console.warn('Plans fetch:', e.message); }
  };

  const fetchHistory = async (cid) => {
    try {
      const h = await getHeaders();
      const r = await fetch(`${API_BASE}/business/subscription/history?company_id=${cid}`, { headers: h });
      const d = await r.json();
      // New endpoint returns { success, history: [...] }; old returned { success, data: [...] }
      if (d.success) setHistory(d.history || d.data || []);
    } catch {}
  };

  // ── Renewal actions (new endpoints) ─────────────────────────────────────────
  const refreshSubscription = async () => {
    if (!bizId) return;
    try {
      const h = await getHeaders();
      const r = await fetch(`${API_BASE}/business/subscription?company_id=${bizId}`, { headers: h });
      const d = await r.json();
      if (d.success && d.subscription) {
        setSub(d.subscription);
        await AsyncStorage.setItem('business_subscription', JSON.stringify(d.subscription));
      }
      await fetchHistory(bizId);
    } catch (e) { console.warn('refreshSubscription:', e.message); }
  };

  // Poll Stripe (via our backend) to see if the external checkout was paid.
  // The plan is only applied server-side once Stripe reports payment_status: 'paid'.
  const checkPaymentStatus = async (sessionId, { silent = false } = {}) => {
    if (!sessionId || checkingPay) return;
    setCheckingPay(true);
    try {
      const h = await getHeaders();
      const r = await fetch(`${API_BASE}/plan/change/status?session_id=${encodeURIComponent(sessionId)}`, { headers: h });
      const d = await r.json().catch(() => ({}));
      if (d.success && d.paid) {
        const planName = pendingPay?.planName || d.subscription?.plan_id || 'your new plan';
        setPendingPay(null);
        await refreshSubscription();
        setTab('current');
        Alert.alert('✅ Payment received', `You're now on ${planName}.`);
      } else if (!silent) {
        Alert.alert('Payment not completed yet', 'We haven\'t seen your payment land. If you just paid, give it a few seconds and tap "I\'ve completed payment" again.');
      }
    } catch (e) {
      if (!silent) Alert.alert('Could not check payment', e?.message || 'Please try again.');
    } finally {
      setCheckingPay(false);
    }
  };

  // When the user returns to the app from the Stripe browser tab, auto-check.
  useEffect(() => {
    const unsub = navigation?.addListener?.('focus', () => {
      if (pendingPay?.sessionId) checkPaymentStatus(pendingPay.sessionId, { silent: true });
    });
    return unsub;
  }, [navigation, pendingPay]);

  const handleRenewNow = async () => {
    if (!subscription || !bizId) return;
    Alert.alert(
      'Renew Subscription',
      `Extend your ${subscription.plan_name || 'plan'} by one ${subscription.billing_cycle || 'month'}?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Renew',
          onPress: async () => {
            try {
              const h = await getHeaders();
              const r = await fetch(`${API_BASE}/business/subscription/renew?company_id=${bizId}`, {
                method: 'POST', headers: h,
                body: JSON.stringify({ notes: 'Manual renewal from mobile app' }),
              });
              const d = await r.json();
              if (d.success) {
                await refreshSubscription();
                Alert.alert('Renewed', 'Your subscription has been extended.');
              } else {
                Alert.alert('Renewal failed', d.error || 'Please try again');
              }
            } catch (e) {
              Alert.alert('Renewal failed', e.message);
            }
          },
        },
      ]
    );
  };

  const handleToggleAutoRenew = async (enabled) => {
    if (!bizId) return;
    try {
      const h = await getHeaders();
      const r = await fetch(`${API_BASE}/business/subscription/auto-renew?company_id=${bizId}`, {
        method: 'PUT', headers: h,
        body: JSON.stringify({ enabled }),
      });
      const d = await r.json();
      if (d.success) {
        await refreshSubscription();
      } else {
        Alert.alert('Could not update', d.error || 'Please try again');
      }
    } catch (e) {
      Alert.alert('Could not update', e.message);
    }
  };

  const handleCancelSubscription = () => {
    if (!subscription || !bizId) return;
    Alert.alert(
      'Cancel Subscription?',
      `You'll keep access until ${fmtDate(subscription.end_date)}. Auto-renewal will be turned off.`,
      [
        { text: 'Keep Subscription', style: 'cancel' },
        {
          text: 'Cancel Subscription',
          style: 'destructive',
          onPress: async () => {
            try {
              const h = await getHeaders();
              const r = await fetch(`${API_BASE}/business/subscription/cancel?company_id=${bizId}`, {
                method: 'POST', headers: h,
                body: JSON.stringify({ reason: 'Cancelled from mobile app' }),
              });
              const d = await r.json();
              if (d.success) {
                await refreshSubscription();
                Alert.alert('Cancelled', d.message || 'Your subscription has been cancelled.');
              } else {
                Alert.alert('Could not cancel', d.error || 'Please try again');
              }
            } catch (e) {
              Alert.alert('Could not cancel', e.message);
            }
          },
        },
      ]
    );
  };

  const handleResumeSubscription = async () => {
    if (!bizId) return;
    try {
      const h = await getHeaders();
      const r = await fetch(`${API_BASE}/business/subscription/resume?company_id=${bizId}`, {
        method: 'POST', headers: h,
      });
      const d = await r.json();
      if (d.success) {
        await refreshSubscription();
        Alert.alert('Resumed', 'Auto-renewal is back on.');
      } else {
        Alert.alert('Could not resume', d.error || 'Please try again');
      }
    } catch (e) {
      Alert.alert('Could not resume', e.message);
    }
  };

  const handleSelectPlan = (plan) => {
    setModalPlan(plan);
    setShowModal(true);
  };

  const handleConfirmPlan = async () => {
    if (!modalPlan) return;
    if (submitting.current) return;

    // Extra lock: prevent re-entry if same plan payment is already in-flight
    const lockKey = `upgrade_lock_${bizId}_${modalPlan.plan_id}`;
    const locked  = await AsyncStorage.getItem(lockKey);
    if (locked && Date.now() - parseInt(locked) < 30000) {
      console.warn('[handleConfirmPlan] Already processing — ignoring duplicate tap');
      return;
    }
    await AsyncStorage.setItem(lockKey, String(Date.now()));

    submitting.current = true;
    setShowModal(false);
    setUpgrading(modalPlan.plan_id);
    try {
      const h = await getHeaders();
      const cycle = billing === 'annual' ? 'annual' : 'monthly';

      // Ask the server to change the plan. It validates the plan and seat limits,
      // computes the prorated amount due, and decides whether payment is required:
      //   - net <= 0 (free / lateral / covered by credit) -> applied immediately
      //   - net  > 0 -> returns requires_payment + a hosted Stripe Checkout URL,
      //                 and does NOT change the plan until payment is confirmed.
      const res = await fetch(`${API_BASE}/plan/change`, {
        method: 'POST',
        headers: h,
        body: JSON.stringify({
          company_id:    bizId,
          plan_id:       modalPlan.plan_id,
          billing_cycle: cycle,
        }),
      });
      const data = await res.json().catch(() => ({}));

      // Any non-OK / unsuccessful response shows an alert instead of crashing.
      // Covers: seat_limit (409), Invalid plan_id (404), missing fields (400), 500s.
      if (!res.ok || !data.success) {
        const msg = data.message || data.error || 'Could not change your plan. Please try again.';
        Alert.alert('Plan change failed', msg);
        return;
      }

      // PAID upgrade: open Stripe's hosted checkout in the browser. The plan is
      // NOT active yet — we remember the session and finalize via /status when the
      // user returns (auto on focus, or via the "I've completed payment" button).
      if (data.requires_payment && data.checkout_url) {
        setPendingPay({ sessionId: data.session_id, planName: modalPlan.name });
        let opened = true;
        try { await Linking.openURL(data.checkout_url); } catch (e) { opened = false; }
        if (opened === false) {
          Alert.alert('Could not open checkout', 'Please try again, or check your internet connection.');
        } else {
          Alert.alert(
            'Complete your payment',
            `A secure Stripe checkout has opened in your browser for ${modalPlan.name}. After paying, return to the app — your plan will activate automatically.`
          );
        }
        return;
      }

      // FREE change (net <= 0): already applied server-side. Refresh and confirm.
      await refreshSubscription();
      Alert.alert('✅ Plan updated', `You're now on ${modalPlan.name}.`);
    } catch (e) {
      Alert.alert('Plan change failed', e?.message || 'Network error. Please try again.');
    } finally {
      submitting.current = false;
      setUpgrading(null);
      try { await AsyncStorage.removeItem(lockKey); } catch {}
    }
  };

  // Called AFTER payment succeeds
  // ONE-TIME PAYMENT: only records the transaction + invoice
  // Subscription DB record is NOT changed — plan stays as-is
  const handleStripeSuccess = async (result) => {
    setShowStripe(false);
    if (!pendingSubData) return;
    const { plan, upgradeInfo } = pendingSubData;
    const stripePaymentId = result?.paymentIntent?.id || `mock_${Date.now()}`;
    try {
      const h = await getHeaders();

      // 1. Record transaction — payment proof
      await fetch(`${API_BASE}/data/transactions`, {
        method: 'POST', headers: h,
        body: JSON.stringify({
          company_id:        bizId,
          type:              'subscription',
          amount:            (result.amount || upgradeInfo.chargeAmount * 100) / 100,
          currency:          'GBP',
          status:            'completed',
          description:       `${upgradeInfo.isUpgrade ? 'Upgrade' : 'Subscription'}: ${plan.name}`,
          stripe_payment_id: stripePaymentId,
          completed_at:      new Date().toISOString(),
        }),
      });

      // 2. Create paid invoice — records what was charged
      const invResult = await autoCreateSubscriptionInvoice(
        { id: subscription?.id, company_id: bizId },
        plan,
        { ...upgradeInfo, stripePaymentId, status: 'paid' }
      );

      // 3. Subscription DB record stays unchanged — only update if this is a brand new subscription
      if (!subscription?.id) {
        // First time subscriber — create subscription record
        const body = {
          company_id:  bizId,
          plan_id:      plan.plan_id,
          plan_name:    plan.name,
          plan_type:    plan.plan_type,
          price:        parseFloat(plan.price),
          billing_cycle:billing,
          max_users:    plan.max_users,
          features:     plan.features,
          status:       'active',
          start_date:   new Date().toISOString().split('T')[0],
          end_date:     new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0],
        };
        const res  = await fetch(`${API_BASE}/business-subscriptions`, { method:'POST', headers:h, body:JSON.stringify(body) });
        const data = await res.json();
        if (data.success) {
          const newSub = data.data || { ...body, id: data.id };
          setSub(newSub);
          await AsyncStorage.setItem('business_subscription', JSON.stringify(newSub));
          onPlanSelected?.(newSub);
        }
      } else {
        // Existing subscriber upgrading — update plan in DB
        const putBody = {
          plan_id:      plan.plan_id,
          plan_name:    plan.name,
          plan_type:    plan.plan_type || 'single_user',
          price:        parseFloat(plan.price),
          price_paid:   parseFloat(plan.price),
          max_users:    plan.max_users,
          features:     plan.features,
          billing_cycle:billing,
          status:       'active',
          start_date:   new Date().toISOString().split('T')[0],
          end_date:     new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0],
        };
        const putRes = await fetch(`${API_BASE}/business-subscriptions/${subscription.id}`, {
          method: 'PUT', headers: h,
          body: JSON.stringify(putBody),
        });
        const putData = await putRes.json();

        // Use server response if available — it has correct plan data from subscription_plans
        // Fall back to locally built object if server fails
        let updated;
        if (putData.success && putData.data) {
          const serverSub = putData.data;
          updated = {
            id:            subscription.id,
            company_id:   bizId,
            plan_id:       serverSub.plan_id   || plan.plan_id,
            plan_name:     serverSub.plan_name  || plan.name,
            plan_type:     serverSub.plan_type  || plan.plan_type,
            price:         parseFloat(serverSub.price_paid || serverSub.price || plan.price),
            price_paid:    parseFloat(serverSub.price_paid || plan.price),
            billing_cycle: serverSub.billing_cycle || billing,
            max_users:     serverSub.users_count || plan.max_users,
            features:      Array.isArray(serverSub.features)
                           ? serverSub.features
                           : Array.isArray(plan.features) ? plan.features : [],
            status:        'active',
            start_date:    serverSub.start_date || new Date().toISOString().split('T')[0],
            end_date:      serverSub.end_date,
          };
          console.log(`[Sub] DB updated → ${updated.plan_id} £${updated.price_paid || updated.price}/${updated.billing_cycle === 'yearly' ? 'yr' : 'mo'}`);
        } else {
          console.warn('[Sub] DB PUT failed or no data:', putData.error);
          updated = { ...subscription, ...putBody, id: subscription.id, company_id: bizId };
        }

        setSub(updated);
        await AsyncStorage.setItem('business_subscription', JSON.stringify(updated));
        onPlanSelected?.(updated);
      }

      setPendingSub(null);

      // Build final subscription — read from AsyncStorage which has just been updated
      let finalSub;
      try {
        const cached = await AsyncStorage.getItem('business_subscription');
        finalSub = cached ? JSON.parse(cached) : null;
      } catch {}
      if (!finalSub) {
        finalSub = !subscription?.id
          ? { plan_id: plan.plan_id, plan_name: plan.name, price: parseFloat(plan.price),
              max_users: plan.max_users, features: plan.features, status: 'active',
              billing_cycle: billing, company_id: bizId }
          : { ...subscription, plan_id: plan.plan_id, plan_name: plan.name,
              price: parseFloat(plan.price), max_users: plan.max_users };
      }

      // Update AsyncStorage with new plan so WelcomeBusiness + Dashboard show new data
      await AsyncStorage.setItem('business_subscription', JSON.stringify(finalSub));

      const charged = (result.amount || upgradeInfo.chargeAmount * 100) / 100;

      Alert.alert(
        '✅ Payment Successful',
        `£${charged.toFixed(2)} paid.
${upgradeInfo.isUpgrade
          ? `Plan upgraded: ${upgradeInfo.previousPlan} → ${plan.name}`
          : `${plan.name} activated`}.`,
        [
          {
            text: 'View Invoice',
            onPress: () => navigation.navigate('BusinessPaymentSystem', {
              subscription: finalSub, companyProfile,
              fromUpgrade: true, initialTab: 'sub_invoices',
            }),
          },
          {
            text: 'Go to Dashboard',
            onPress: () => {
              // Navigate back to WelcomeBusiness with updated subscription
              // Use reset so user can't go back to subscription screen mid-flow
              navigation.reset({
                index: 0,
                routes: [{
                  name: 'WelcomeBusiness',
                  params: {
                    companyProfile,
                    subscription: finalSub,
                    refreshed: true,
                  },
                }],
              });
            },
          },
        ]
      );
    } catch (e) {
      Alert.alert('Error', 'Payment received but records failed to update. Contact support.');
    } finally {
      setUpgrading(null);
      submitting.current = false;
      // Clear upgrade lock
      try { await AsyncStorage.removeItem(`upgrade_lock_${bizId}_${modalPlan?.plan_id}`); } catch {}
    }
  };

  const handleStripeFailure = (error) => {
    setShowStripe(false);
    setPendingSub(null);
    setUpgrading(null);
    submitting.current = false;
    Alert.alert('Payment Failed', error?.message || 'Your payment could not be processed. Please try again.');
  };

  const autoCreateSubscriptionInvoice = async (sub, plan, upgradeInfo = {}) => {
    try {
      const {
        chargeAmount,
        isUpgrade    = false,
        isDowngrade  = false,
        previousPlan = null,
        previousPrice = 0,
        newPrice      = parseFloat(plan.price || 0),
      } = upgradeInfo;

      // Use chargeAmount if provided (upgrade diff), else full plan price
      const billAmount = parseFloat(chargeAmount ?? newPrice);

      // Guard — skip if nothing to charge
      if (billAmount <= 0) {
        console.warn('[Sub Invoice] Skipped — charge amount is £0');
        return;
      }

      // Guard — only check for duplicates if creating a pending invoice
      // (paid invoices are always created since payment already happened)
      const invoiceStatus = upgradeInfo.status || 'pending';
      if (invoiceStatus === 'pending' && sub.id) {
        const h2  = await getHeaders();
        const chk = await fetch(
          `${API_BASE}/data/subscription_invoices?company_id=${bizId}&subscription_type=business`,
          { headers: h2 }
        );
        const cd = await chk.json();
        const dup = (cd.data || []).some(i =>
          String(i.subscription_id) === String(sub.id) &&
          i.plan_id === plan.plan_id &&
          i.status === 'pending'
        );
        if (dup) {
          console.log('[Sub Invoice] Duplicate detected — skipping');
          return;
        }
      }

      // Build description
      const actionLabel = isUpgrade ? 'Upgrade' : isDowngrade ? 'Downgrade' : 'Subscription';
      const prevDesc    = previousPlan && isUpgrade
        ? ` (from ${previousPlan} £${previousPrice}/mo)`
        : '';
      const description = isUpgrade
        ? `Plan Upgrade${prevDesc} → ${plan.name} £${newPrice}/mo`
        : isDowngrade
        ? `Plan Change${prevDesc} → ${plan.name} £${newPrice}/mo`
        : `${plan.name} — ${billing === 'annual' ? 'Annual' : 'Monthly'} Subscription`;

      const h          = await getHeaders();
      const invoiceNum = `SINV-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`;

      const res = await fetch(`${API_BASE}/data/subscription_invoices`, {
        method: 'POST', headers: h,
        body: JSON.stringify({
          invoice_number:    invoiceNum,
          company_id:        parseInt(bizId) || null,
          company_id:       parseInt(bizId) || null,
          subscription_id:   sub.id || null,
          plan_id:           plan.plan_id,
          plan_name:         plan.name,
          billing_cycle:     billing,
          amount:            billAmount,
          tax_rate:          20,
          currency:          'GBP',
          status:            invoiceStatus,
          paid_at:           invoiceStatus === 'paid' ? new Date().toISOString() : null,
          stripe_payment_id: upgradeInfo.stripePaymentId || null,
          subscription_type: 'business',
          notes:             description,
        }),
      });
      const data = await res.json();
      if (data.success) {
        const total = data.data?.total || (billAmount * 1.2).toFixed(2);
        console.log(`[Sub Invoice] ${invoiceNum} | ${actionLabel} | Charge: £${billAmount} | Total incl VAT: £${total}`);
      }
    } catch (e) {
      console.warn('[autoCreateSubscriptionInvoice]', e.message);
    }
  };

  // Business plans only — professional and learning plans have their own screens
  const BUSINESS_PLAN_IDS = [
    'single_starter', 'single_professional', 'single_enterprise',
    'team_starter',   'team_professional',   'team_enterprise',
    'multi_team',     'multi_business',       'multi_enterprise',
  ];

  const filteredPlans = plans.filter(p => {
    // Must be in the business plan whitelist
    if (!BUSINESS_PLAN_IDS.includes(p.plan_id)) return false;
    // Solo plans: single_* 
    const isSingle = p.plan_id.startsWith('single_');
    return planType === 'single' ? isSingle : !isSingle;
  }).sort((a,b) => parseFloat(a.price) - parseFloat(b.price));

  const annualPrice = (p) => (parseFloat(p.price) * 12 * 0.85).toFixed(2); // 15% off annual

  const renderPlanCard = (plan, idx) => {
    const isCurrentPlan = subscription?.plan_id === plan.plan_id;
    const isUpgrading   = upgrading === plan.plan_id;
    const gradColors    = PLAN_COLORS[plan.plan_id] || [C.primary, C.p2];
    const isPop         = plan.name?.includes('Professional') || plan.name?.includes('Pro');
    const features      = Array.isArray(plan.features) ? plan.features : [];
    const displayPrice  = billing === 'annual' ? annualPrice(plan) : plan.price;
    const savings       = billing === 'annual' ? (parseFloat(plan.price) * 12 * 0.15).toFixed(2) : null;

    return (
      <Animated.View
        key={plan.plan_id}
        style={[
          styles.planCard,
          isPop && styles.planCardPopular,
          isCurrentPlan && styles.planCardCurrent,
          { opacity: fadeAnim, transform: [{ translateY: fadeAnim.interpolate({ inputRange:[0,1], outputRange:[30,0] }) }] },
        ]}
      >
        {isPop && (
          <View style={styles.popularBadge}>
            <MaterialCommunityIcons name="star" size={10} color="#fff"/>
            <Text style={styles.popularBadgeText}>MOST POPULAR</Text>
          </View>
        )}
        {isCurrentPlan && (
          <View style={[styles.popularBadge, { backgroundColor: C.success }]}>
            <MaterialCommunityIcons name="check-circle" size={10} color="#fff"/>
            <Text style={styles.popularBadgeText}>CURRENT PLAN</Text>
          </View>
        )}

        <LinearGradient colors={gradColors} style={styles.planGradient} start={{x:0,y:0}} end={{x:1,y:1}}>
          <View style={styles.planHeader}>
            <MaterialCommunityIcons
              name={PLAN_ICONS[plan.plan_id] || 'briefcase-outline'}
              size={28} color="rgba(255,255,255,0.9)"
            />
            <View style={styles.planHeaderText}>
              <Text style={styles.planName}>{plan.name}</Text>
              <Text style={styles.planUsers}>
                {plan.max_users >= 999 ? 'Unlimited users' : `Up to ${plan.max_users} user${plan.max_users > 1 ? 's':''}`}
              </Text>
            </View>
          </View>
          <View style={styles.planPriceRow}>
            <Text style={styles.planPriceCurrency}>£</Text>
            <Text style={styles.planPrice}>{displayPrice}</Text>
            <Text style={styles.planPricePer}>/{billing === 'annual' ? 'yr' : 'mo'}</Text>
          </View>
          {savings && (
            <View style={styles.savingsBadge}>
              <Text style={styles.savingsText}>Save £{savings}/yr</Text>
            </View>
          )}
        </LinearGradient>

        <View style={styles.planBody}>
          {features.slice(0, 6).map((f, i) => (
            <View key={i} style={styles.featureRow}>
              <View style={styles.featureDot}/>
              <Text style={styles.featureText}>{f}</Text>
            </View>
          ))}
          {features.length > 6 && (
            <Text style={styles.moreFeaturesText}>+{features.length - 6} more features</Text>
          )}

          <TouchableOpacity
            style={[
              styles.selectBtn,
              isCurrentPlan && styles.selectBtnCurrent,
              { backgroundColor: isCurrentPlan ? C.success : gradColors[0] },
            ]}
            onPress={() => !isCurrentPlan && handleSelectPlan(plan)}
            disabled={isCurrentPlan || isUpgrading}
          >
            {isUpgrading ? (
              <ActivityIndicator size="small" color="#fff"/>
            ) : (
              <>
                <MaterialCommunityIcons
                  name={isCurrentPlan ? 'check-circle' : 'arrow-up-circle'}
                  size={16} color="#fff"
                />
                <Text style={styles.selectBtnText}>
                  {isCurrentPlan ? 'Active Plan' : subscription ? 'Switch to This' : 'Get Started'}
                </Text>
              </>
            )}
          </TouchableOpacity>
        </View>
      </Animated.View>
    );
  };

  const renderExpiryBanner = () => {
    if (!subscription?.end_date) return null;
    // Prefer the server-computed flags; fall back to client-side calc for legacy responses
    const days = subscription.days_until_expiry ?? daysUntil(subscription.end_date);
    if (days === null || days > 14) return null;
    const isGrace = subscription.is_in_grace_period === true;
    const isExpired = subscription.is_expired === true || days <= 0;
    const urgent = isExpired || isGrace || days <= 3;
    return (
      <LinearGradient
        colors={urgent ? ['#EF4444','#DC2626'] : ['#F59E0B','#D97706']}
        style={styles.expiryBanner}
        start={{x:0,y:0}} end={{x:1,y:0}}
      >
        <MaterialCommunityIcons name={urgent ? 'alert' : 'clock-alert-outline'} size={20} color="#fff"/>
        <View style={{ flex:1, marginLeft:10 }}>
          <Text style={styles.expiryTitle}>
            {isExpired ? 'Subscription Expired'
             : isGrace ? 'Past Due — Grace Period'
             : `Expires in ${days} day${days !== 1 ? 's' : ''}`}
          </Text>
          <Text style={styles.expirySubtitle}>
            {isExpired ? 'Renew now to restore access'
             : isGrace ? 'Renew now to avoid losing access'
             : 'Renew now to avoid interruption'}
          </Text>
        </View>
        <TouchableOpacity
          style={styles.expiryBtn}
          onPress={handleRenewNow}
        >
          <Text style={styles.expiryBtnText}>Renew</Text>
        </TouchableOpacity>
      </LinearGradient>
    );
  };

  const renderCurrentPlan = () => {
    if (!subscription) {
      return (
        <View style={styles.emptyState}>
          <MaterialCommunityIcons name="crown-outline" size={64} color={C.light}/>
          <Text style={styles.emptyTitle}>No Active Subscription</Text>
          <Text style={styles.emptySub}>Choose a plan to unlock all features</Text>
          <TouchableOpacity style={styles.emptyBtn} onPress={() => setTab('plans')}>
            <Text style={styles.emptyBtnText}>View Plans</Text>
          </TouchableOpacity>
        </View>
      );
    }
    const gradColors = PLAN_COLORS[subscription.plan_id] || [C.primary, C.p2];
    const days = daysUntil(subscription.end_date);
    const progress = days !== null ? Math.max(0, Math.min(100, (30 - days) / 30 * 100)) : 0;
    const features = Array.isArray(subscription.features) ? subscription.features
                   : Array.isArray(subscription.plan_features) ? subscription.plan_features : [];

    return (
      <ScrollView showsVerticalScrollIndicator={false}>
        <LinearGradient colors={gradColors} style={styles.currentCard} start={{x:0,y:0}} end={{x:1,y:1}}>
          <View style={styles.currentTop}>
            <View>
              <Text style={styles.currentLabel}>ACTIVE PLAN</Text>
              <Text style={styles.currentPlanName}>{subscription.plan_name || subscription.plan_id}</Text>
            </View>
            <View style={[styles.statusPill, { backgroundColor: subscription.status === 'active' ? C.success : C.warning }]}>
              <Text style={styles.statusPillText}>{(subscription.status || 'active').toUpperCase()}</Text>
            </View>
          </View>
          <View style={styles.currentMetaRow}>
            <View style={styles.currentMetaItem}>
              <Text style={styles.currentMetaLabel}>Monthly Cost</Text>
              <Text style={styles.currentMetaValue}>£{parseFloat(subscription.price_paid || subscription.price || 0).toLocaleString('en-GB')}</Text>
            </View>
            <View style={styles.currentMetaItem}>
              <Text style={styles.currentMetaLabel}>Users</Text>
              <Text style={styles.currentMetaValue}>{subscription.max_users >= 999 ? '∞' : subscription.max_users || 1}</Text>
            </View>
            <View style={styles.currentMetaItem}>
              <Text style={styles.currentMetaLabel}>Renews</Text>
              <Text style={styles.currentMetaValue}>{fmtDate(subscription.end_date)}</Text>
            </View>
          </View>
          {days !== null && days <= 30 && (
            <View style={styles.progressContainer}>
              <View style={styles.progressBar}>
                <View style={[styles.progressFill, { width: `${progress}%`, backgroundColor: days <= 7 ? '#EF4444' : '#FDB913' }]}/>
              </View>
              <Text style={styles.progressText}>
                {days <= 0 ? 'Expired' : `${days} days remaining`}
              </Text>
            </View>
          )}
          <View style={styles.currentActions}>
            <TouchableOpacity style={styles.currentActionBtn} onPress={() => setTab('plans')}>
              <MaterialCommunityIcons name="swap-horizontal" size={16} color="#fff"/>
              <Text style={styles.currentActionText}>Change Plan</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.currentActionBtn, { backgroundColor: 'rgba(255,255,255,0.15)' }]}
              onPress={() => navigation.navigate('BusinessPaymentSystem', { subscription, companyProfile })}
            >
              <MaterialCommunityIcons name="credit-card-outline" size={16} color="#fff"/>
              <Text style={styles.currentActionText}>Manage Billing</Text>
            </TouchableOpacity>
          </View>
        </LinearGradient>

        {features.length > 0 && (
          <View style={styles.featuresCard}>
            <Text style={styles.featuresTitle}>Included Features</Text>
            <View style={styles.featuresGrid}>
              {features.map((f, i) => (
                <View key={i} style={styles.featureChip}>
                  <MaterialCommunityIcons name="check-circle" size={14} color={C.success}/>
                  <Text style={styles.featureChipText}>{f}</Text>
                </View>
              ))}
            </View>
          </View>
        )}

        <View style={styles.billingCard}>
          <Text style={styles.billingTitle}>Billing Details</Text>
          <View style={styles.billingRow2}>
            <Text style={styles.billingKey}>Plan</Text>
            <Text style={styles.billingVal}>{subscription.plan_name}</Text>
          </View>
          <View style={styles.billingRow2}>
            <Text style={styles.billingKey}>Billing Cycle</Text>
            <Text style={styles.billingVal}>{subscription.billing_cycle || 'Monthly'}</Text>
          </View>
          <View style={styles.billingRow2}>
            <Text style={styles.billingKey}>Start Date</Text>
            <Text style={styles.billingVal}>{fmtDate(subscription.start_date)}</Text>
          </View>
          <View style={styles.billingRow2}>
            <Text style={styles.billingKey}>Next Billing</Text>
            <Text style={styles.billingVal}>{fmtDate(subscription.end_date)}</Text>
          </View>
          <View style={styles.billingRow2}>
            <Text style={styles.billingKey}>Amount</Text>
            <Text style={[styles.billingVal, { color: C.primary, fontWeight: '700' }]}>£{parseFloat(subscription.price_paid || subscription.price || 0).toLocaleString('en-GB')}/{subscription.billing_cycle === 'yearly' ? 'yr' : 'mo'}</Text>
          </View>
        </View>

        {/* Renewal management — new endpoints */}
        <View style={styles.billingCard}>
          <Text style={styles.billingTitle}>Renewal</Text>

          {/* Auto-renew toggle */}
          <View style={styles.renewalRow}>
            <View style={{ flex: 1 }}>
              <Text style={styles.renewalRowTitle}>Auto-renew</Text>
              <Text style={styles.renewalRowSub}>
                {subscription.auto_renew
                  ? `Renews automatically ${subscription.next_renewal_at ? `on ${fmtDate(subscription.next_renewal_at)}` : 'before expiry'}`
                  : 'Will not renew automatically — will end on ' + fmtDate(subscription.end_date)}
              </Text>
            </View>
            <Switch
              value={!!subscription.auto_renew}
              onValueChange={(v) => handleToggleAutoRenew(v)}
              trackColor={{ false: '#E2E8F0', true: C.success }}
              thumbColor="#fff"
              disabled={!!subscription.cancelled_at}
            />
          </View>

          {/* Renew now button */}
          <TouchableOpacity style={styles.renewalPrimaryBtn} onPress={handleRenewNow}>
            <MaterialCommunityIcons name="autorenew" size={18} color="#fff"/>
            <Text style={styles.renewalPrimaryText}>Renew Now</Text>
          </TouchableOpacity>

          {/* Cancel / Resume */}
          {subscription.cancelled_at ? (
            <View style={styles.cancelledBanner}>
              <MaterialCommunityIcons name="information-outline" size={18} color={C.warning}/>
              <View style={{ flex: 1, marginLeft: 10 }}>
                <Text style={styles.cancelledTitle}>Cancellation scheduled</Text>
                <Text style={styles.cancelledSub}>Access ends on {fmtDate(subscription.end_date)}</Text>
              </View>
              <TouchableOpacity style={styles.resumeBtn} onPress={handleResumeSubscription}>
                <Text style={styles.resumeBtnText}>Resume</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <TouchableOpacity style={styles.cancelBtn} onPress={handleCancelSubscription}>
              <MaterialCommunityIcons name="close-circle-outline" size={18} color={C.danger}/>
              <Text style={styles.cancelBtnText}>Cancel Subscription</Text>
            </TouchableOpacity>
          )}
        </View>
      </ScrollView>
    );
  };

  const renderHistory = () => {
    // Action metadata: maps action codes from /history endpoint to UI presentation
    const ACTION_META = {
      renewal:              { label: 'Renewed',          icon: 'autorenew',          bg: '#D1FAE5', color: C.success },
      cancellation:         { label: 'Cancelled',        icon: 'cancel',             bg: '#FEE2E2', color: C.danger },
      resume:               { label: 'Resumed',          icon: 'play-circle',        bg: '#D1FAE5', color: C.success },
      auto_renew_enabled:   { label: 'Auto-renew on',    icon: 'toggle-switch',      bg: '#EFF6FF', color: C.primary },
      auto_renew_disabled:  { label: 'Auto-renew off',   icon: 'toggle-switch-off',  bg: '#FEF3C7', color: C.warning },
      upgraded:             { label: 'Upgraded',         icon: 'arrow-up-circle',    bg: '#D1FAE5', color: C.success },
      downgraded:           { label: 'Downgraded',       icon: 'arrow-down-circle',  bg: '#FEE2E2', color: C.danger },
    };

    return (
      <View style={{ flex:1 }}>
        {history.length === 0 ? (
          <View style={styles.emptyState}>
            <MaterialCommunityIcons name="history" size={48} color={C.light}/>
            <Text style={styles.emptyTitle}>No History Yet</Text>
            <Text style={styles.emptySub}>Subscription changes will appear here</Text>
          </View>
        ) : (
          <FlatList
            data={history}
            keyExtractor={(item, i) => String(item.id || i)}
            contentContainerStyle={{ padding: 16 }}
            renderItem={({ item }) => {
              const meta = ACTION_META[item.action] || { label: item.action || 'Change', icon: 'swap-horizontal', bg: '#EFF6FF', color: C.primary };
              const subText = item.old_plan_id && item.new_plan_id
                ? `${item.old_plan_id} → ${item.new_plan_id}`
                : item.notes || (item.triggered_by === 'auto' ? 'Automatic' : item.actor_name ? `by ${item.actor_name}` : null);
              return (
                <View style={styles.historyCard}>
                  <View style={[styles.historyIcon, { backgroundColor: meta.bg }]}>
                    <MaterialCommunityIcons name={meta.icon} size={20} color={meta.color}/>
                  </View>
                  <View style={{ flex: 1, marginLeft: 12 }}>
                    <Text style={styles.historyAction}>{meta.label}</Text>
                    {subText && <Text style={styles.historyDetail}>{subText}</Text>}
                    <Text style={styles.historyDate}>{fmtDate(item.created_at)}</Text>
                  </View>
                  {item.new_price > 0 && (
                    <Text style={styles.historyPrice}>£{item.new_price}/{item.billing_cycle === 'yearly' ? 'yr' : 'mo'}</Text>
                  )}
                </View>
              );
            }}
          />
        )}
      </View>
    );
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={C.primary}/>
          <Text style={styles.loadingText}>Loading plans...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor={C.primary}/>

      {/* Header */}
      <LinearGradient colors={[C.primary, C.p2]} style={styles.header}>
        <TouchableOpacity style={styles.backBtn} onPress={() => navigation?.goBack()}>
          <Ionicons name="arrow-back" size={22} color="#fff"/>
        </TouchableOpacity>
        <View style={{ flex:1 }}>
          <Text style={styles.headerTitle}>Subscription Plans</Text>
          <Text style={styles.headerSub}>{companyProfile?.name || 'JOBENG Business'}</Text>
        </View>
        {subscription && (
          <View style={styles.headerBadge}>
            <Text style={styles.headerBadgeText}>{subscription.plan_name?.split(' ')[0] || 'Active'}</Text>
          </View>
        )}
      </LinearGradient>

      {/* Expiry Banner */}
      {renderExpiryBanner()}

      {/* Awaiting external Stripe checkout */}
      {pendingPay && (
        <View style={styles.pendingPayBanner}>
          <MaterialCommunityIcons name="clock-alert-outline" size={20} color={C.warning} />
          <View style={{ flex: 1 }}>
            <Text style={styles.pendingPayTitle}>Payment in progress</Text>
            <Text style={styles.pendingPaySub}>Finish paying for {pendingPay.planName} in your browser, then come back.</Text>
          </View>
          <TouchableOpacity
            style={styles.pendingPayBtn}
            onPress={() => checkPaymentStatus(pendingPay.sessionId)}
            disabled={checkingPay}
          >
            {checkingPay
              ? <ActivityIndicator size="small" color="#fff" />
              : <Text style={styles.pendingPayBtnText}>I've completed payment</Text>}
          </TouchableOpacity>
        </View>
      )}

      {/* Tab Bar */}
      <View style={styles.tabBar}>
        {[
          { id:'plans', label:'Plans', icon:'view-grid-outline' },
          { id:'current', label:'My Plan', icon:'crown-outline' },
          { id:'history', label:'History', icon:'history' },
        ].map(t => (
          <TouchableOpacity
            key={t.id}
            style={[styles.tabItem, tab === t.id && styles.tabItemActive]}
            onPress={() => setTab(t.id)}
          >
            <MaterialCommunityIcons name={t.icon} size={18} color={tab === t.id ? C.primary : C.light}/>
            <Text style={[styles.tabLabel, tab === t.id && styles.tabLabelActive]}>{t.label}</Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* Plans Tab */}
      {tab === 'plans' && (
        <Animated.View style={{ flex:1, opacity: fadeAnim }}>
          {/* Type + billing toggles */}
          <View style={styles.toggleRow}>
            <View style={styles.typeToggle}>
              {[{id:'single', label:'Solo'},{id:'team', label:'Team'}].map(t => (
                <TouchableOpacity
                  key={t.id}
                  style={[styles.typeBtn, planType === t.id && styles.typeBtnActive]}
                  onPress={() => setPlanType(t.id)}
                >
                  <Text style={[styles.typeBtnText, planType === t.id && styles.typeBtnTextActive]}>{t.label}</Text>
                </TouchableOpacity>
              ))}
            </View>
            <TouchableOpacity
              style={styles.billingToggle}
              onPress={() => setBilling(b => b === 'monthly' ? 'annual' : 'monthly')}
            >
              <MaterialCommunityIcons name={billing === 'annual' ? 'calendar-check' : 'calendar-outline'} size={14} color={billing === 'annual' ? C.success : C.sub}/>
              <Text style={[styles.billingToggleText, billing === 'annual' && { color: C.success }]}>
                {billing === 'annual' ? 'Annual (2 mo free)' : 'Monthly'}
              </Text>
            </TouchableOpacity>
          </View>

          <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{ padding:16, gap:16 }}>
            {filteredPlans.length === 0 ? (
              <View style={styles.emptyState}>
                <Text style={styles.emptyTitle}>No plans available</Text>
              </View>
            ) : filteredPlans.map((p, i) => renderPlanCard(p, i))}
            <View style={{ height: 40 }}/>
          </ScrollView>
        </Animated.View>
      )}

      {tab === 'current' && (
        <View style={{ flex:1 }}>
          {renderCurrentPlan()}
        </View>
      )}

      {tab === 'history' && renderHistory()}

      {/* Stripe Payment Modal */}
      {stripeScenario && (
        <Modal visible={showStripe} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => handleStripeFailure({ message: 'Payment cancelled' })}>
          <StripePaymentIntegration
            scenario={stripeScenario}
            onPaymentSuccess={handleStripeSuccess}
            onPaymentFailure={handleStripeFailure}
            onCancel={() => handleStripeFailure({ message: 'Payment cancelled' })}
          />
        </Modal>
      )}

      {/* Confirm Plan Modal */}
      <Modal visible={showModal} animationType="slide" transparent onRequestClose={() => setShowModal(false)}>
        <View style={styles.modalOverlay}>
          <View style={styles.confirmModal}>
            <LinearGradient
              colors={modalPlan ? (PLAN_COLORS[modalPlan.plan_id] || [C.primary, C.p2]) : [C.primary, C.p2]}
              style={styles.confirmHeader}
            >
              <MaterialCommunityIcons name="crown" size={32} color="#fff"/>
              <Text style={styles.confirmTitle}>{modalPlan?.name}</Text>
              <Text style={styles.confirmPrice}>£{modalPlan?.price}/{billing === 'yearly' ? 'yr' : 'mo'}</Text>
            </LinearGradient>
            <View style={styles.confirmBody}>
              <Text style={styles.confirmSubtitle}>
                {subscription ? `Switch from ${subscription.plan_name} to ${modalPlan?.name}?` : `Activate ${modalPlan?.name}?`}
              </Text>
              {subscription && modalPlan && (() => {
                const cur  = parseFloat(subscription.price_paid || subscription.price || 0);
                const next = parseFloat(modalPlan.price || 0);
                const diff = next - cur;
                const isUp = diff > 0;
                const isDn = diff < 0;
                if (diff === 0) return null;
                return (
                  <View style={[styles.upgradeNote, { backgroundColor: isUp ? '#EFF6FF' : '#F0FDF4' }]}>
                    <MaterialCommunityIcons name={isUp ? 'arrow-up-circle' : 'arrow-down-circle'} size={16} color={isUp ? C.primary : C.success}/>
                    <View style={{ flex:1, marginLeft:8 }}>
                      <Text style={[styles.upgradeNoteText, { fontWeight:'700', marginBottom:3, color: isUp ? C.primary : C.success }]}>
                        {isUp ? 'Upgrade Charge' : 'Downgrade — Credit'}: £{Math.abs(diff).toFixed(2)}/mo
                      </Text>
                      <Text style={[styles.upgradeNoteText, { color: C.sub }]}>
                        {isUp
                          ? `You'll only be charged the difference (£${cur}/mo → £${next}/mo). Invoice for £${diff.toFixed(2)} + 20% VAT = £${(diff*1.2).toFixed(2)} will be generated.`
                          : `Your new lower rate of £${next}/mo takes effect immediately.`}
                      </Text>
                    </View>
                  </View>
                );
              })()}
              <View style={styles.confirmActions}>
                <TouchableOpacity style={styles.confirmCancelBtn} onPress={() => setShowModal(false)}>
                  <Text style={styles.confirmCancelText}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.confirmOkBtn} onPress={handleConfirmPlan}>
                  <Text style={styles.confirmOkText}>Confirm</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea:            { flex:1, backgroundColor: C.bg },
  loadingContainer:    { flex:1, justifyContent:'center', alignItems:'center', gap:12 },
  loadingText:         { color: C.sub, fontSize:14 },
  header:              { flexDirection:'row', alignItems:'center', paddingHorizontal:16, paddingVertical:14, paddingTop:20, gap:12 },
  backBtn:             { width:36, height:36, borderRadius:18, backgroundColor:'rgba(255,255,255,0.15)', justifyContent:'center', alignItems:'center' },
  headerTitle:         { color:'#fff', fontSize:17, fontWeight:'700' },
  headerSub:           { color:'rgba(255,255,255,0.75)', fontSize:12, marginTop:1 },
  headerBadge:         { backgroundColor:'rgba(255,255,255,0.2)', paddingHorizontal:10, paddingVertical:4, borderRadius:12 },
  headerBadgeText:     { color:'#fff', fontSize:11, fontWeight:'700' },
  expiryBanner:        { flexDirection:'row', alignItems:'center', padding:12, paddingHorizontal:16 },
  expiryTitle:         { color:'#fff', fontWeight:'700', fontSize:13 },
  expirySubtitle:      { color:'rgba(255,255,255,0.85)', fontSize:11, marginTop:2 },
  expiryBtn:           { backgroundColor:'rgba(255,255,255,0.25)', paddingHorizontal:14, paddingVertical:6, borderRadius:16 },
  expiryBtnText:       { color:'#fff', fontWeight:'700', fontSize:12 },
  pendingPayBanner:    { flexDirection:'row', alignItems:'center', gap:10, backgroundColor:'#FFFBEB', marginHorizontal:16, marginTop:12, padding:12, borderRadius:14, borderWidth:1, borderColor:'#FDE68A' },
  pendingPayTitle:     { fontSize:13, fontWeight:'700', color:'#92400E' },
  pendingPaySub:       { fontSize:11, color:'#B45309', marginTop:2 },
  pendingPayBtn:       { backgroundColor:C.primary, paddingHorizontal:12, paddingVertical:9, borderRadius:10, minWidth:96, alignItems:'center' },
  pendingPayBtnText:   { color:'#fff', fontWeight:'700', fontSize:11 },
  tabBar:              { flexDirection:'row', backgroundColor:'#fff', borderBottomWidth:1, borderBottomColor:C.border },
  tabItem:             { flex:1, alignItems:'center', paddingVertical:10, gap:3, borderBottomWidth:2, borderBottomColor:'transparent' },
  tabItemActive:       { borderBottomColor: C.primary },
  tabLabel:            { fontSize:11, color:C.light, fontWeight:'600' },
  tabLabelActive:      { color: C.primary },
  toggleRow:           { flexDirection:'row', alignItems:'center', justifyContent:'space-between', padding:12, paddingHorizontal:16, backgroundColor:'#fff', borderBottomWidth:1, borderBottomColor:C.border },
  typeToggle:          { flexDirection:'row', backgroundColor: C.bg, borderRadius:20, padding:3 },
  typeBtn:             { paddingHorizontal:16, paddingVertical:6, borderRadius:16 },
  typeBtnActive:       { backgroundColor:'#fff', shadowColor:'#000', shadowOpacity:0.08, shadowRadius:4, elevation:2 },
  typeBtnText:         { fontSize:13, color:C.sub, fontWeight:'600' },
  typeBtnTextActive:   { color: C.text },
  billingToggle:       { flexDirection:'row', alignItems:'center', gap:5, backgroundColor:C.bg, paddingHorizontal:12, paddingVertical:6, borderRadius:16 },
  billingToggleText:   { fontSize:12, color:C.sub, fontWeight:'600' },
  planCard:            { backgroundColor:'#fff', borderRadius:20, overflow:'hidden', shadowColor:'#000', shadowOpacity:0.08, shadowRadius:12, elevation:4 },
  planCardPopular:     { borderWidth:2, borderColor: C.purple },
  planCardCurrent:     { borderWidth:2, borderColor: C.success },
  popularBadge:        { position:'absolute', top:0, right:0, zIndex:10, flexDirection:'row', alignItems:'center', gap:4, backgroundColor:C.purple, paddingHorizontal:10, paddingVertical:4, borderBottomLeftRadius:12 },
  popularBadgeText:    { color:'#fff', fontSize:9, fontWeight:'800', letterSpacing:0.8 },
  planGradient:        { padding:20 },
  planHeader:          { flexDirection:'row', alignItems:'center', gap:12, marginBottom:16 },
  planHeaderText:      { flex:1 },
  planName:            { color:'#fff', fontSize:17, fontWeight:'800' },
  planUsers:           { color:'rgba(255,255,255,0.8)', fontSize:12, marginTop:2 },
  planPriceRow:        { flexDirection:'row', alignItems:'flex-end', gap:3 },
  planPriceCurrency:   { color:'rgba(255,255,255,0.9)', fontSize:18, fontWeight:'700', marginBottom:4 },
  planPrice:           { color:'#fff', fontSize:40, fontWeight:'900', lineHeight:44 },
  planPricePer:        { color:'rgba(255,255,255,0.75)', fontSize:14, marginBottom:6 },
  savingsBadge:        { alignSelf:'flex-start', backgroundColor:'rgba(255,255,255,0.25)', paddingHorizontal:10, paddingVertical:3, borderRadius:12, marginTop:8 },
  savingsText:         { color:'#fff', fontSize:11, fontWeight:'700' },
  planBody:            { padding:20, gap:10 },
  featureRow:          { flexDirection:'row', alignItems:'center', gap:10 },
  featureDot:          { width:6, height:6, borderRadius:3, backgroundColor: C.primary },
  featureText:         { fontSize:13, color:C.text, flex:1 },
  moreFeaturesText:    { fontSize:12, color:C.sub, fontStyle:'italic', marginTop:4 },
  selectBtn:           { flexDirection:'row', alignItems:'center', justifyContent:'center', gap:8, paddingVertical:14, borderRadius:14, marginTop:8 },
  selectBtnCurrent:    { backgroundColor: C.success },
  selectBtnText:       { color:'#fff', fontWeight:'700', fontSize:15 },
  emptyState:          { alignItems:'center', padding:48, gap:12 },
  emptyTitle:          { fontSize:18, fontWeight:'700', color:C.text },
  emptySub:            { fontSize:14, color:C.sub, textAlign:'center' },
  emptyBtn:            { marginTop:8, backgroundColor:C.primary, paddingHorizontal:24, paddingVertical:12, borderRadius:12 },
  emptyBtnText:        { color:'#fff', fontWeight:'700' },
  currentCard:         { margin:16, borderRadius:20, padding:20, overflow:'hidden' },
  currentTop:          { flexDirection:'row', justifyContent:'space-between', alignItems:'flex-start', marginBottom:16 },
  currentLabel:        { color:'rgba(255,255,255,0.7)', fontSize:10, fontWeight:'700', letterSpacing:1.2, textTransform:'uppercase' },
  currentPlanName:     { color:'#fff', fontSize:22, fontWeight:'800', marginTop:4 },
  statusPill:          { paddingHorizontal:10, paddingVertical:4, borderRadius:12 },
  statusPillText:      { color:'#fff', fontSize:10, fontWeight:'800', letterSpacing:0.8 },
  currentMetaRow:      { flexDirection:'row', gap:8, marginBottom:16 },
  currentMetaItem:     { flex:1, backgroundColor:'rgba(255,255,255,0.15)', borderRadius:12, padding:12, alignItems:'center' },
  currentMetaLabel:    { color:'rgba(255,255,255,0.7)', fontSize:10, textTransform:'uppercase', letterSpacing:0.5 },
  currentMetaValue:    { color:'#fff', fontSize:16, fontWeight:'800', marginTop:4 },
  progressContainer:   { marginBottom:16 },
  progressBar:         { height:4, backgroundColor:'rgba(255,255,255,0.3)', borderRadius:2, overflow:'hidden', marginBottom:6 },
  progressFill:        { height:'100%', borderRadius:2 },
  progressText:        { color:'rgba(255,255,255,0.8)', fontSize:11 },
  currentActions:      { flexDirection:'row', gap:10 },
  currentActionBtn:    { flex:1, flexDirection:'row', alignItems:'center', justifyContent:'center', gap:6, backgroundColor:'rgba(255,255,255,0.2)', paddingVertical:10, borderRadius:12 },
  currentActionText:   { color:'#fff', fontWeight:'600', fontSize:13 },
  featuresCard:        { backgroundColor:'#fff', marginHorizontal:16, marginBottom:12, borderRadius:16, padding:16 },
  featuresTitle:       { fontSize:15, fontWeight:'700', color:C.text, marginBottom:12 },
  featuresGrid:        { flexDirection:'row', flexWrap:'wrap', gap:8 },
  featureChip:         { flexDirection:'row', alignItems:'center', gap:6, backgroundColor:C.bg, paddingHorizontal:10, paddingVertical:6, borderRadius:20 },
  featureChipText:     { fontSize:12, color:C.text, fontWeight:'500' },
  billingCard:         { backgroundColor:'#fff', marginHorizontal:16, marginBottom:24, borderRadius:16, padding:16 },
  billingTitle:        { fontSize:15, fontWeight:'700', color:C.text, marginBottom:12 },
  billingRow2:         { flexDirection:'row', justifyContent:'space-between', paddingVertical:8, borderBottomWidth:1, borderBottomColor:C.border },
  billingKey:          { fontSize:13, color:C.sub },
  billingVal:          { fontSize:13, color:C.text, fontWeight:'600' },
  renewalRow:          { flexDirection:'row', alignItems:'center', paddingVertical:12, borderBottomWidth:1, borderBottomColor:C.border, marginBottom:8 },
  renewalRowTitle:     { fontSize:14, fontWeight:'700', color:C.text, marginBottom:2 },
  renewalRowSub:       { fontSize:12, color:C.sub, lineHeight:16 },
  renewalPrimaryBtn:   { flexDirection:'row', alignItems:'center', justifyContent:'center', backgroundColor:C.primary, borderRadius:12, paddingVertical:12, gap:8, marginTop:8 },
  renewalPrimaryText:  { color:'#fff', fontSize:14, fontWeight:'700' },
  cancelBtn:           { flexDirection:'row', alignItems:'center', justifyContent:'center', backgroundColor:'#FEF2F2', borderRadius:12, paddingVertical:12, gap:8, marginTop:12, borderWidth:1, borderColor:'#FECACA' },
  cancelBtnText:       { color:C.danger, fontSize:14, fontWeight:'600' },
  cancelledBanner:     { flexDirection:'row', alignItems:'center', backgroundColor:'#FFFBEB', borderRadius:12, padding:12, marginTop:12, borderWidth:1, borderColor:'#FDE68A' },
  cancelledTitle:      { fontSize:13, fontWeight:'700', color:'#92400E' },
  cancelledSub:        { fontSize:12, color:'#92400E', marginTop:2 },
  resumeBtn:           { backgroundColor:C.success, paddingHorizontal:14, paddingVertical:8, borderRadius:20 },
  resumeBtnText:       { color:'#fff', fontSize:12, fontWeight:'700' },
  historyCard:         { flexDirection:'row', alignItems:'center', backgroundColor:'#fff', borderRadius:14, padding:14, marginBottom:10, shadowColor:'#000', shadowOpacity:0.04, shadowRadius:6, elevation:2 },
  historyIcon:         { width:40, height:40, borderRadius:20, justifyContent:'center', alignItems:'center' },
  historyAction:       { fontSize:14, fontWeight:'700', color:C.text, textTransform:'capitalize' },
  historyDetail:       { fontSize:12, color:C.sub, marginTop:2 },
  historyDate:         { fontSize:11, color:C.light, marginTop:3 },
  historyPrice:        { fontSize:14, fontWeight:'700', color:C.primary },
  modalOverlay:        { flex:1, backgroundColor:'rgba(0,0,0,0.5)', justifyContent:'flex-end' },
  confirmModal:        { backgroundColor:'#fff', borderTopLeftRadius:24, borderTopRightRadius:24, overflow:'hidden' },
  confirmHeader:       { alignItems:'center', padding:28, gap:8 },
  confirmTitle:        { color:'#fff', fontSize:20, fontWeight:'800' },
  confirmPrice:        { color:'rgba(255,255,255,0.85)', fontSize:15 },
  confirmBody:         { padding:24 },
  confirmSubtitle:     { fontSize:15, color:C.text, textAlign:'center', marginBottom:16 },
  upgradeNote:         { flexDirection:'row', alignItems:'flex-start', gap:8, backgroundColor:'#EFF6FF', padding:12, borderRadius:12, marginBottom:16 },
  upgradeNoteText:     { fontSize:12, color:C.primary, flex:1, lineHeight:18 },
  confirmActions:      { flexDirection:'row', gap:12 },
  confirmCancelBtn:    { flex:1, backgroundColor:C.bg, paddingVertical:14, borderRadius:14, alignItems:'center' },
  confirmCancelText:   { color:C.sub, fontWeight:'700' },
  confirmOkBtn:        { flex:1, backgroundColor:C.primary, paddingVertical:14, borderRadius:14, alignItems:'center' },
  confirmOkText:       { color:'#fff', fontWeight:'700', fontSize:15 },
});